import * as React from "react"
import {useTheme} from "@mui/material";
import {AmchartSeeker, CHART_DEFAULT_COLORS} from "@ui-seeker/core";
import {isEmpty} from "@ui-seeker/utils"

import {DATE_METRICS} from "@constants";
import {usePriceEvolutionChartPricing, usePriceEvolutionChart} from "../../@hooks";
import {PriceEvolutionChartMetric, PriceEvolutionCurrentTabId} from "../../@signals";
import {DATE_FORMAT, DATE_FORMAT_RECEIVED, TIME_UNIT} from "./@constants";
import PriceEvolutionChartLoadingBlock from "./LoadingBlock";
import PriceEvolutionChartSubtitle from "./Subtitle";



const colorFactory = (key, palette) => {
	if (key === 'ASP ONLINE') return palette.secondary.lighter
	if (key === 'ASP OFFLINE') return palette.primary.main
	if (key === 'Min Price') return palette.primary.light
	if (key === 'Max Price') return palette.primary.light
	if (key === 'Min Discount') return palette.primary.light
	if (key === 'Max Discount') return palette.primary.light
	if (key === 'ASP OMNICHANNEL') return palette.rrsp.lighter
	return palette.primary.lighter
}

export default function PriceEvolutionChartBlock() {
	const { palette } = useTheme()
	const {
		useActualPrice, showLegend, showRRSP, handleChangePrice, handleChangeLegend, handleChangeShowRRSP
	} = usePriceEvolutionChart()
	const {
		isLoading, allData, currentData, selectedLabel, series, currency, colors, isPercentage, isSplitBySku,
		handleChangeMetric, handleExcel
	} = usePriceEvolutionChartPricing()

	if (isLoading)
		return <PriceEvolutionChartLoadingBlock />

	if (!currentData?.length)
		return <></>

	const seriesFiltered = series.filter(sn => {
		if (!sn.includes('RRSP'))
			return true
		return isSplitBySku || showRRSP
	})

	return <AmchartSeeker
		data={currentData}
		title={`RETAILER ${isPercentage ? 'DISCOUNT' : 'PRICE'} EVOLUTION - ${selectedLabel}`}
		subtitle={<PriceEvolutionChartSubtitle
			isSplitBySku={isSplitBySku}
			isPercentage={isPercentage}
			useActualPrice={useActualPrice}
			showLegend={showLegend}
			showRRSP={showRRSP}
			handleChangePrice={handleChangePrice}
			handleChangeLegend={handleChangeLegend}
			handleChangeMetric={handleChangeMetric}
			handleChangeShowRRSP={handleChangeShowRRSP}
		/>}
		series={seriesFiltered.map((key, idx) => ({
			name: key,
			yField: key + (useActualPrice ? '' : 'List'),
			xField: 'dateNorm',
			color: colors[key],
			...(key.includes('RRSP') || key.includes('ASP')) && {
				color: isSplitBySku ? colorFactory(key, palette) : CHART_DEFAULT_COLORS[idx],
				bullets: { hidden: true },
				strokes: { strokeDasharray: [6, 6] }
			},
			...((key.includes('Min ') || key.includes('Max ')) && {
				color: colorFactory(key, palette),
				...key.includes('Min')
					? { strokes: { strokeDasharray: [6, 6] } }
					: {
						openValueYField: 'Min',
						fills: { fillOpacity: 0.2, visible: true }
					}
			}),
			showLegend: showLegend,
			tooltip: {
				useSingleTooltip: true,
				tooltipVisibleOnZero: isPercentage,
				customSort: (a, b) => {
					const aIsASP = a.text.includes('ASP')
					const bIsASP = b.text.includes('ASP')
					if (aIsASP || bIsASP) {
						if (aIsASP && bIsASP) return b.value - a.value
						else if (aIsASP && !bIsASP) return -1
						else if (!aIsASP && bIsASP) return 1
					}
					if (b.text.includes('RRSP')) return b.value
					return b.value - a.value
				}
			}
		}))}
		chartProps={{
			legend: { hidden: !showLegend },
			numberFormat: isPercentage ? "#,###'%'" : `#,###.00 ' ${currency}'`,
			dateFormatReceived: DATE_FORMAT_RECEIVED[PriceEvolutionChartMetric.value],
			xAxis: {
				categoryField: 'dateNorm',
				isDateAxis: PriceEvolutionChartMetric.value !== DATE_METRICS.QUARTERLY,
				dateFormat: DATE_FORMAT[PriceEvolutionChartMetric.value],
				baseInterval: {
					timeUnit: TIME_UNIT[PriceEvolutionChartMetric.value],
					count: 1
				}
			},
			onDownload: handleExcel
		}}
		sx={{ height: 700 }}
	/>
}




